import {General, Users} from '../constants';
import {MarkUnread} from 'mattermost-redux/constants/channels';

import {
    type Channel,
    type ChannelMembership,
    type ChannelMessageCount,
    type ChannelNotifyProps, ChannelType,
    type DirectChannel, type GroupChannel, type TeammateStatus,
} from 'mattermost-redux/types/channels';
import type {UserNotifyProps, UserProfile, UsersState} from 'mattermost-redux/types/users';
import type {GlobalState} from 'types/store';
import type {IDMappedObjects, RelationOneToManyUnique, RelationOneToOne} from 'mattermost-redux/types/utilities';

import type {Team} from '@mattermost/types/teams';

import {isDirectChannel} from 'utils/isDirectChannel';

import {type User} from 'features/users';

import {isGroupChannel} from 'utils/isGroupChannel';

import {displayUsername} from './user_utils';

const channelTypeOrder = {
    [ChannelType.Open]: 0,
    [ChannelType.Private]: 1,
    [ChannelType.Direct]: 2,
    [ChannelType.Group]: 3,
};

/**
 *
 * @deprecated используйте селекторы для получения нужной и нформации о канале:
 * getTeammateId - для получения id собеседника
 * getStatusForUserId - для получения статуса собеседника
 * getChannelDisplayName - для получения отображаемого имени канала
 */
export function completeDirectChannelInfo(
    usersState: UsersState,
    teammateNameDisplay: string,
    channel: Channel,
): Channel {
    if (isDirectChannel(channel)) {
        const teammateId = getUserIdFromChannelName(usersState.currentUserId, channel.name);

        // return empty string instead of `someone` default string for display_name
        const directChannel: DirectChannel = {
            ...channel,
            display_name: displayUsername(usersState.profiles[teammateId], teammateNameDisplay, false),
            teammate_id: teammateId,
            status: usersState.statuses[teammateId] || 'offline',
        };
        return directChannel;
    } else if (isGroupChannel(channel)) {
        return completeDirectGroupInfo(usersState, teammateNameDisplay, channel);
    }

    return channel;
}

export function splitRoles(roles: string): Set<string> {
    return roles ? new Set<string>(roles.split(' ')) : new Set<string>([]);
}

/**
 *
 * @deprecated используйте селекторы для получения нужной и нформации о канале:
 * getTeammateId - для получения id собеседника
 * getStatusForUserId - для получения статуса собеседника
 * getChannelDisplayName - для получения отображаемого имени канала
 *
 * It is a variant of completeDirectChannelInfo that accepts the minimal
 * data required instead of depending on the entirety of state.entities.users. This allows the
 * calling selector to have fewer dependencies, reducing its need to recompute when memoized.
 *
 * Ideally, this would replace completeDirectChannelInfo altogether, but is currently factored out
 * to minimize changes while addressing a critical performance issue.
 */
export function newCompleteDirectChannelInfo(
    currentUserId: string,
    profiles: IDMappedObjects<UserProfile>,
    profilesInChannel: RelationOneToManyUnique<Channel, UserProfile>,
    teammateStatus: TeammateStatus,
    teammateNameDisplay: string,
    channel: Channel,
): Channel {
    if (isDirectChannel(channel)) {
        const teammateId = getUserIdFromChannelName(currentUserId, channel.name);

        // return empty string instead of `someone` default string for display_name
        const directChannel: DirectChannel = {
            ...channel,
            display_name: displayUsername(profiles[teammateId], teammateNameDisplay, false),
            teammate_id: teammateId,
            status: teammateStatus,
        };

        return directChannel;
    } else if (isGroupChannel(channel)) {
        return newCompleteDirectGroupInfo(currentUserId, profiles, profilesInChannel, teammateNameDisplay, channel);
    }

    return channel;
}

export function completeDirectChannelDisplayName(
    currentUserId: string,
    profiles: IDMappedObjects<UserProfile>,
    userIdsInChannel: Set<string>,
    teammateNameDisplay: string,
    channel: Channel,
): Channel {
    if (isDirectChannel(channel)) {
        const dmChannelClone = {...channel};
        const teammateId = getUserIdFromChannelName(currentUserId, channel.name);

        return {
            ...dmChannelClone,
            display_name: displayUsername(profiles[teammateId], teammateNameDisplay),
        };
    } else if (isGroupChannel(channel) && userIdsInChannel && userIdsInChannel.size > 0) {
        const displayName = getGroupDisplayNameFromUserIds(
            userIdsInChannel,
            profiles,
            currentUserId,
            teammateNameDisplay,
        );
        return {...channel, display_name: displayName};
    }

    return channel;
}

export function cleanUpUrlable(input: string): string {
    let cleaned = input.trim().replace(/-/g, ' ').replace(/[^\w\s]/gi, '').toLowerCase().replace(/\s/g, '-');
    cleaned = cleaned.replace(/-{2,}/, '-');
    cleaned = cleaned.replace(/^-+/, '');
    cleaned = cleaned.replace(/-+$/, '');
    return cleaned;
}

export function getChannelByName(channels: IDMappedObjects<Channel>, name: string): Channel | undefined {
    return Object.values(channels).find((channel) => channel.name === name);
}

export function getDirectChannelName(id: string, otherId: string): string {
    let handle;

    if (otherId > id) {
        handle = id + '__' + otherId;
    } else {
        handle = otherId + '__' + id;
    }

    return handle;
}

export function getUserIdFromChannelName(userId: string, channelName: DirectChannel['name']): string {
    const ids = channelName.split('__', 2) as [string, string];

    return ids[0] === userId ? ids[1] : ids[0];
}

export function isSelfTalkChannel(userId: string, channel: Channel | undefined): boolean {
    if (!channel || !isDirectChannel(channel)) {
        return false;
    }

    return userId === getUserIdFromChannelName(userId, channel.name);
}

export function getChannelsIdForTeam(state: GlobalState, teamId: string): string[] {
    const {channels} = state.entities.channels;

    return Object.keys(channels).filter((channelId) => channels[channelId]?.team_id === teamId);
}

export function getGroupDisplayNameFromUserIds(
    userIds: Set<string>,
    profiles: IDMappedObjects<UserProfile>,
    currentUserId: string,
    teammateNameDisplay: string,
    omitCurrentUser = true,
): string {
    const names: string[] = [];
    userIds.forEach((id) => {
        if (!(id === currentUserId && omitCurrentUser)) {
            names.push(displayUsername(profiles[id], teammateNameDisplay));
        }
    });

    function sortUsernames(a: string, b: string) {
        const locale = getUserLocale(currentUserId, profiles);
        return a.localeCompare(b, locale, {numeric: true});
    }

    return names.sort(sortUsernames).join(', ');
}

export function isDefaultChannel(channel: Channel, team?: Team): boolean {
    return channel.id === team?.primary_channel_id;
}

export function getGroupChannelDisplayName(
    currentUserId: string,
    profiles: IDMappedObjects<UserProfile>,
    profilesInChannel: RelationOneToManyUnique<Channel, UserProfile>,
    teammateNameDisplay: string,
    channel: GroupChannel,
    omitCurrentUser?: boolean,
) {
    const profilesIds = profilesInChannel[channel.id];

    if (profilesIds) {
        return getGroupDisplayNameFromUserIds(profilesIds, profiles, currentUserId, teammateNameDisplay, omitCurrentUser);
    }

    const usernames = channel.display_name.split(', ');
    const users = Object.keys(profiles)
        .map((key) => profiles[key])
        .filter((profile): profile is User => Boolean(profile));

    const userIds: Set<string> = new Set();
    usernames.forEach((username: string) => {
        const u = users.find((p): boolean => p.username === username);
        if (u) {
            userIds.add(u.id);
        }
    });
    if (usernames.length === userIds.size) {
        return getGroupDisplayNameFromUserIds(userIds, profiles, currentUserId, teammateNameDisplay, omitCurrentUser);
    }

    return channel.display_name;
}

/**
 *
 * @deprecated используйте getChannelDisplayName - для получения отображаемого имени канала
 */
export function completeDirectGroupInfo(
    usersState: UsersState,
    teammateNameDisplay: string,
    channel: Channel,
    omitCurrentUser = true,
) {
    const {currentUserId, profiles, profilesInChannel} = usersState;
    const profilesIds = profilesInChannel[channel.id];
    const gm = {...channel};

    if (profilesIds) {
        gm.display_name = getGroupDisplayNameFromUserIds(
            profilesIds,
            profiles,
            currentUserId,
            teammateNameDisplay,
            omitCurrentUser,
        );
        return gm;
    }

    const usernames = gm.display_name.split(', ');
    const users = Object.keys(profiles)
        .map((key) => profiles[key])
        .filter((profile): profile is User => Boolean(profile));

    const userIds: Set<string> = new Set();
    usernames.forEach((username: string) => {
        const u = users.find((p): boolean => p.username === username);
        if (u) {
            userIds.add(u.id);
        }
    });
    if (usernames.length === userIds.size) {
        gm.display_name = getGroupDisplayNameFromUserIds(userIds, profiles, currentUserId, teammateNameDisplay);
        return gm;
    }

    return channel;
}

/**
 *
 * @deprecated используйте getChannelDisplayName - для получения отображаемого имени канала
 *
 * It is a variant of completeDirectGroupInfo that accepts the minimal
 * data required instead of depending on the entirety of state.entities.users. This allows the
 * calling selector to have fewer dependencies, reducing its need to recompute when memoized.
 *
 * See also newCompleteDirectChannelInfo.
 */
function newCompleteDirectGroupInfo(
    currentUserId: string,
    profiles: IDMappedObjects<UserProfile>,
    profilesInChannel: RelationOneToManyUnique<Channel, UserProfile>,
    teammateNameDisplay: string,
    channel: GroupChannel,
) {
    const profilesIds = profilesInChannel[channel.id];
    const gm = {...channel};

    if (profilesIds) {
        gm.display_name = getGroupDisplayNameFromUserIds(profilesIds, profiles, currentUserId, teammateNameDisplay);
        return gm;
    }

    const usernames = gm.display_name.split(', ');
    const users = Object.keys(profiles)
        .map((key) => profiles[key])
        .filter((profile): profile is User => Boolean(profile));

    const userIds: Set<string> = new Set();
    usernames.forEach((username: string) => {
        const u = users.find((p): boolean => p.username === username);
        if (u) {
            userIds.add(u.id);
        }
    });
    if (usernames.length === userIds.size) {
        gm.display_name = getGroupDisplayNameFromUserIds(userIds, profiles, currentUserId, teammateNameDisplay);
        return gm;
    }

    return channel;
}

export function sortChannelsByTypeListAndDisplayName(
    locale: string,
    typeList: string[],
    a: Channel,
    b: Channel,
): number {
    const idxA = typeList.indexOf(a.type);
    const idxB = typeList.indexOf(b.type);

    if (idxA === -1 && idxB !== -1) {
        return 1;
    }
    if (idxB === -1 && idxA !== -1) {
        return -1;
    }

    if (idxA !== idxB) {
        if (idxA < idxB) {
            return -1;
        }
        return 1;
    }

    const aDisplayName = filterName(a.display_name);
    const bDisplayName = filterName(b.display_name);

    if (aDisplayName !== bDisplayName) {
        return aDisplayName.toLowerCase().localeCompare(bDisplayName.toLowerCase(), locale, {numeric: true});
    }

    return a.name.toLowerCase().localeCompare(b.name.toLowerCase(), locale, {numeric: true});
}

export function sortChannelsByTypeAndDisplayName(locale: string, a: Channel, b: Channel): number {
    if (channelTypeOrder[a.type] !== channelTypeOrder[b.type]) {
        if (channelTypeOrder[a.type] < channelTypeOrder[b.type]) {
            return -1;
        }

        return 1;
    }

    const aDisplayName = filterName(a.display_name);
    const bDisplayName = filterName(b.display_name);

    if (aDisplayName !== bDisplayName) {
        return aDisplayName.toLowerCase().localeCompare(bDisplayName.toLowerCase(), locale, {numeric: true});
    }

    return a.name.toLowerCase().localeCompare(b.name.toLowerCase(), locale, {numeric: true});
}

function filterName(name: string): string {
    return name.replace(/[.,'"\/#!$%\^&\*;:{}=\-_`~()]/g, ''); // eslint-disable-line no-useless-escape
}

export function sortChannelsByDisplayName(locale: string, a: Channel, b: Channel): number {
    // if both channels have the display_name defined
    if (a.display_name && b.display_name && a.display_name !== b.display_name) {
        return a.display_name.toLowerCase().localeCompare(b.display_name.toLowerCase(), locale, {numeric: true});
    }

    return a.name.toLowerCase().localeCompare(b.name.toLowerCase(), locale, {numeric: true});
}

export function sortChannelsByDisplayNameAndMuted(
    locale: string,
    members: RelationOneToOne<Channel, ChannelMembership>,
    a: Channel,
    b: Channel,
): number {
    const aMember = members[a.id];
    const bMember = members[b.id];

    if (isChannelMuted(bMember) === isChannelMuted(aMember)) {
        return sortChannelsByDisplayName(locale, a, b);
    }

    if (!isChannelMuted(bMember) && isChannelMuted(aMember)) {
        return 1;
    }

    return -1;
}

export function isChannelMuted(member?: ChannelMembership): boolean {
    return member?.notify_props ? member.notify_props.mark_unread === MarkUnread.MENTION : false;
}

export function areChannelMentionsIgnored(
    channelMemberNotifyProps: ChannelNotifyProps,
    currentUserNotifyProps: UserNotifyProps,
) {
    let ignoreChannelMentionsDefault = Users.IGNORE_CHANNEL_MENTIONS_OFF;

    if (currentUserNotifyProps.channel && currentUserNotifyProps.channel === 'false') {
        ignoreChannelMentionsDefault = Users.IGNORE_CHANNEL_MENTIONS_ON;
    }

    let ignoreChannelMentions = channelMemberNotifyProps && channelMemberNotifyProps.ignore_channel_mentions;
    if (!ignoreChannelMentions || ignoreChannelMentions === Users.IGNORE_CHANNEL_MENTIONS_DEFAULT) {
        ignoreChannelMentions = ignoreChannelMentionsDefault as any;
    }

    return ignoreChannelMentions !== Users.IGNORE_CHANNEL_MENTIONS_OFF;
}

function getUserLocale(userId: string, profiles: IDMappedObjects<UserProfile>) {
    let locale = General.DEFAULT_LOCALE;
    const userLocale = profiles[userId]?.locale;
    if (userLocale) {
        locale = userLocale;
    }

    return locale;
}

export function filterChannelsMatchingTerm(channels: Channel[], term: string): Channel[] {
    const lowercasedTerm = term.toLowerCase();

    return channels.filter((channel: Channel): boolean => {
        if (!channel) {
            return false;
        }
        const name = (channel.name || '').toLowerCase();
        const displayName = (channel.display_name || '').toLowerCase();

        return name.startsWith(lowercasedTerm) || displayName.startsWith(lowercasedTerm);
    });
}

export function channelListToMap(channelList: Channel[]): IDMappedObjects<Channel> {
    return channelList.reduce<Record<string, Channel>>((channels, channel) => {
        channels[channel.id] = channel;
        return channels;
    }, {});
}

/**
 * Returns an object containing the number of unread mentions/messages in a channel and whether
 * or not that channel would be shown as unread in the sidebar.
 */
export function calculateUnreadCount(messageCount?: ChannelMessageCount, member?: ChannelMembership) {
    if (!member || !messageCount) {
        return {
            showUnread: false,
            mentions: 0,
            messages: 0,
        };
    }

    const mentions = member.mention_count_root;
    const messages = messageCount.root - member.msg_count_root;

    return {
        showUnread: mentions > 0 || (!isChannelMuted(member) && messages > 0),
        messages,
        mentions,
    };
}
